import React, { useRef, useState } from 'react';

import { useClickAway } from 'react-use';

import CloseIcon from '@pkg/assets/icons/v2/close.svg?react';
import { ChipColor, ChipMember, CircleButton, RhythmScoreBadge } from '@pkg/components';
import { DepAssetHandler } from '@pkg/components/deprecated';
import { useAppSelector, useBreakpoints } from '@pkg/hooks';

import { useAllYats } from '@app/store/zustand';
import { useProfileNotificationsStore } from '@app/store/zustand/profile-page/useProfileNotificationsStore';
import { getCurrentEmojiData, useEmojisStore } from '@app/store/zustand/useEmojisStore.ts';

import { cardMotion, wrapperMotion } from '../components/InfoModal/InfoModal.motion';
import {
    Backdrop,
    Card,
    CloseButtonWrapper,
    Content,
    RhythmWrapper,
    Wrapper,
    YatBox,
    YatBoxWrapper,
} from '../components/InfoModal/InfoModal.style';

interface YatMedia {
    image?: string;
    webm?: string;
    mediaError?: boolean;
}

export interface ShowcaseYatModalProps {
    onClose?: () => void;
}

const FALLBACK_MEDIA: YatMedia = { image: '', webm: '', mediaError: false };

export function ShowcaseYatModal({ onClose }: ShowcaseYatModalProps) {
    const { isMobile } = useBreakpoints();

    const wrapperRef = useRef(null);
    const [mediaError, setMediaError] = useState(false);

    const { selectedYat, setOpenShowcaseYatModal } = useProfileNotificationsStore();

    // TODO: We should not have to traverse every single `emoji` in a user's
    // account in order to retrieve the relevant data... there must be a better way!
    const userId = useAppSelector((state) => state.yatPage.user_id);
    const { data } = useAllYats({ userId });
    const yats = data?.data?.yats?.value;

    const extendedEmojis = useEmojisStore((state) => state.extendedEmojis);
    const { currentGeneration, currentRhythmScore } = getCurrentEmojiData(selectedYat ?? '', extendedEmojis);

    const currentYat = yats?.find(({ eid }) => eid === selectedYat);
    const media: YatMedia = currentYat ? currentYat.media : FALLBACK_MEDIA;

    function handleClose() {
        setOpenShowcaseYatModal(false);
        onClose?.();
    }

    useClickAway(wrapperRef, handleClose);

    const assetMarkup =
        (media?.image || media?.webm) && !mediaError ? (
            <DepAssetHandler onError={() => setMediaError(true)} videoUrl={media?.webm} imageUrl={media?.image} />
        ) : (
            <YatBox>
                <ChipMember yat={selectedYat} color={ChipColor.Transparent} size={isMobile ? 'small' : 'large'} />
            </YatBox>
        );

    // Should we alternatively indicate if a Yat was awarded for `free`?
    const rhythmScoreMarkup = currentRhythmScore ? (
        <RhythmWrapper>
            <RhythmScoreBadge generation={currentGeneration} score={currentRhythmScore} badgeStyle="bare" />
        </RhythmWrapper>
    ) : null;

    // TODO: Ideally, we re-use something like `MainStage / NFTMedia`
    // instead of shoving this content into a modal.
    return (
        <Wrapper {...wrapperMotion}>
            <Card {...cardMotion} ref={wrapperRef}>
                <CloseButtonWrapper>
                    <CircleButton
                        size={isMobile ? 'small' : 'medium'}
                        variant="solid"
                        ariaLabel="Close modal"
                        onClick={handleClose}
                    >
                        <CloseIcon aria-hidden="true" />
                    </CircleButton>
                </CloseButtonWrapper>

                <Content>
                    <YatBoxWrapper>{assetMarkup}</YatBoxWrapper>
                    {rhythmScoreMarkup}
                </Content>
            </Card>

            <Backdrop onClick={onClose} />
        </Wrapper>
    );
}
